import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CloseIcon = ({ size = 20, className = "" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';

const ActivityViewModal = ({ open, onClose, activityId }) => {
  const [activityDetails, setActivityDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (open) {
      setIsMaximized(false);
      const code = typeof activityId === 'string' ? activityId : (activityId?.Code);
      if (code) {
        fetchActivityDetails(code);
      }
    }
  }, [open, activityId]);

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const fetchActivityDetails = async (code) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/activity-master/${code}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      setActivityDetails(response.data);
    } catch (error) {
      console.error('Error fetching activity details:', error);
      setError('Failed to load activity details');
      setActivityDetails(null);
    } finally {
      setLoading(false);
    }
  };

  if (!open || !activityId) return null;

  const activity = activityDetails || activityId;
  const isActive = activity.Active === 1 || activity.Active === true;
  const statusText = isActive ? 'Active' : 'Inactive';
  const statusStyle = isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  const isParent = activity.IsParentActivity === 1 || activity.IsParentActivity === true;

  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? "opacity-100 visible" : "opacity-0 invisible"}`}>
      <style>
        {`
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          .hide-scrollbar::-webkit-scrollbar { display: none; }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className={`bg-white shadow-2xl flex flex-col transition-all duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? 'w-full h-full max-w-none m-0 rounded-none' : 'w-full max-w-6xl'} h-full rounded-none`}>
        {/* Header */}
        <div className="flex-shrink-0 p-8 pb-6 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">Activity Details</h2>
            <div className="flex gap-2">
              <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={toggleMaximize}>
                {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
              </button>
              <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-hidden flex flex-col">
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>
            ) : (
              <div className="space-y-6 pb-6">
                {/* General Info */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">General Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Activity Code</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase font-semibold">{activity.Code || '-'}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Description</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{activity.Description || '-'}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Type</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{activity.Type || '-'}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Status</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] flex items-center">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${statusStyle}`}>{statusText}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Is Parent Activity</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] flex items-center">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${isParent ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                          {isParent ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hierarchy Info */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">Hierarchy & Parent</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Parent Activity Code</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{activity.ParentActivityCode || '-'}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Parent Activity Name</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{activity.ParentActivityName || '-'}</div>
                    </div>
                  </div>
                </div>

                {/* Project Info */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">Project Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Project No</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase font-semibold">{activity.ProjectNo || '-'}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Project Description</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">{activity.ProjectDescription || '-'}</div>
                    </div>
                  </div>
                </div>
                
                {/* Audit Info */}
                 <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">Audit Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                     <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Created At</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                        {activity.CreatedAt ? new Date(activity.CreatedAt).toLocaleString() : '-'}
                      </div>
                    </div>
                     <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-[#64748b]">Updated At</label>
                      <div className="border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                         {activity.UpdatedAt ? new Date(activity.UpdatedAt).toLocaleString() : '-'}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button type="button" className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityViewModal;